/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 공통 클래스를 정의한다.
 *
 * @file /src/models/table/Table.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 4.
 */
import * as parent from '@components/panel/Panel';
import { ListStores, TreeStores } from '@models/store/Store';
import { Header } from '@models/table/Header';
import { Body } from '@models/table/Body';
import { Column, Properties as ColumnProperties } from '@models/table/Column';
import { Grouper } from '@models/table/Group';
import { Row } from '@models/table/Row';
import { Record } from '@models/store/Record';
import { Selection } from '@models/table/Selection';
import { Menu } from '@components/menu/Menu';
import { Language, LanguageText } from '@utils/Language';
import { Utils } from '@utils/Utils';
import './Table.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 컴포넌트가 보여질 떄
     */
    show?: (me: T) => void;

    /**
     * 데이터가 로드되기 전
     */
    beforeLoad?: (me: T) => void;

    /**
     * 데이터가 로드되었을 때
     */
    load?: (me: T) => void;

    /**
     * 데이터가 변경되었을 때
     */
    update?: (me: T) => void;

    /**
     * 선택항목이 변경되었을 때
     */
    selectionChange?: (selections: Record[], me: T) => void;

    /**
     * ROW 가 확장되었을 때
     */
    rowExpand?: (row: Row, me: T) => void;

    /**
     * ROW 를 클릭하였을 때
     */
    rowClick?: (row: Row, is_selection_changed: boolean, me: T) => void;

    /**
     * ROW 를 더블클릭하여 오픈할 때
     */
    openItem?: (record: Record, me: T, dataIndex: string, columnIndex: number) => void;

    /**
     * ROW 를 우클릭하여 메뉴를 오픈할 때
     */
    openMenu?: (menu: Menu, selections: Record[], me: T) => void;

    /**
     * 레코드가 변경되었을 때
     */
    edit?: (record: Record, me: T) => void;
}

export interface Properties<S extends ListStores | TreeStores, T extends parent.Panel> extends parent.Properties<T> {
    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 컬럼 목록
     */
    columns: ColumnProperties<T>[];

    /**
     * 그룹설정
     */
    grouper?: Grouper<T>;

    /**
     * 객체가 랜더링된 후 데이터를 자동으로 불러올지 여부
     */
    autoLoad?: boolean;

    /**
     * 고정할 컬럼 수
     */
    freeze?: number;

    /**
     * 선택자 설정
     */
    selection?: Omit<Selection['properties'], 'table'> | boolean;

    /**
     * 빈 데이터 메시지
     */
    emptyText?: LanguageText;

    /**
     * 행 포커스 가능여부
     */
    focusableRow?: boolean;

    /**
     * 컬럼 포커스 가능여부
     */
    focusableCell?: boolean;

    /**
     * 합계 설정 여부
     */
    summary?: boolean;

    /**
     * 행 지연렌더링 여부
     */
    isLazyRender?: boolean;

    /**
     * 레코드에 따라 ROW 에 추가할 클래스
     */
    setRowClass?: (record: Record) => string | string[] | null | undefined;

    /**
     * 렌더링 완료 후 로딩상태로 보일지 여부
     */
    loading?: boolean;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Table<
    S extends ListStores | TreeStores = ListStores | TreeStores,
    T extends Table<any, any> = Table<any, any>,
>
    extends parent.Panel
{
    /**
     * 헤더
     */
    header: Header | null = null;

    /**
     * 바디
     */
    body: Body;

    /**
     * 고정 컬럼 수
     */
    freeze: number = 0;

    /**
     * 고정된 컬럼 너비 (flex-basis 계산용)
     */
    freezeFlexBasis: { px: number; rem: number } = { px: 0, rem: 0 };
    freezeFlexGrow: number = 0;
    freezeStyle: string = null;

    /**
     * 데이터스토어
     */
    store: S;

    /**
     * 컬럼 목록 (최상위, 트리 구조)
     */
    columns: Column[] = [];

    /**
     * 그룹설정
     */
    grouper: Grouper<T> = null;

    /**
     * 객체가 랜더링된 후 데이터를 자동으로 불러올지 여부
     */
    autoLoad: boolean = false;

    /**
     * 선택자
     */
    selection: Selection;

    /**
     * 합계 설정 여부
     */
    summary: boolean | null = null;

    /**
     * 컬럼 에디터 비활성화여부
     */
    disableEditor: boolean = false;

    /**
     * 테이블 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<S, T> = null) {
        super(properties);

        this.store = this.properties.store;
        if ((this.properties.grouper ?? null) !== null) {
            this.store.setGroup({
                field: this.properties.grouper.dataIndex,
                sorters: this.properties.grouper.sorters,
            });
        }

        this.store.addEvent('beforeLoad', async () => {
            await this.onBeforeLoad();
        });
        this.store.addEvent('load', async () => {
            await this.onLoad();
        });
        this.store.addEvent('update', async () => {
            await this.onUpdate();
        });
        this.store.addEvent('edit', async (record) => {
            await this.onEdit(record);
        });
        this.store.retain();

        this.autoLoad = this.properties?.autoLoad !== false;
        this.scrollable = this.properties.scrollable ?? true;
        this.summary = this.properties.summary === true;
        this.keyboard = true;

        this.freeze = this.properties?.freeze ?? 0;

        if ((this.properties.grouper ?? null) !== null) {
            this.grouper = {
                dataIndex: properties.grouper.dataIndex,
                sorters: properties.grouper.sorters,
                summary: properties.grouper.summary ?? null,
                renderer: properties.grouper.renderer ?? null,
            };
        } else {
            this.grouper = null;
        }

        this.initColumns(this.properties.columns);

        this.body = new Body({
            table: this,
            emptyText: this.properties.emptyText ?? null,
            focusableRow: this.properties.focusableRow !== false,
            focusableCell: this.properties.focusableCell !== false,
            isLazyRender: this.properties.isLazyRender === true,
        });

        this.properties.selection ??= true;
        this.selection = new Selection(
            typeof this.properties.selection == 'boolean'
                ? { table: this, selectable: this.properties.selection }
                : { table: this, ...this.properties.selection }
        );

        this.properties.loadingText = this.properties.loadingText ?? Language.getAction('LOADING');
    }

    /**
     * 데이터스토어를 가져온다.
     *
     * @returns 데이터스토어
     */
    getStore(): S {
        return this.store;
    }

    /**
     * 헤더를 가져온다.
     *
     * @returns 헤더
     */
    getHeader(): Header | null {
        return this.header;
    }

    /**
     * 컬럼 에디터 비활성화여부를 설정한다.
     *
     * @param disableEditor - 컬럼 에디터 비활성화여부
     */
    setDisableEditor(disableEditor: boolean): void {
        this.disableEditor = disableEditor;
    }

    /**
     * 컬럼 에디터가 비활성화되어 있는지 확인한다.
     *
     * @returns 컬럼 에디터 비활성화여부
     */
    isEditorDisabled(): boolean {
        return this.disableEditor;
    }

    /**
     * 바디를 가져온다.
     *
     * @returns 바디
     */
    getBody(): Body {
        return this.body;
    }

    /**
     * 선택자를 가져온다.
     *
     * @returns 선택자
     */
    getSelection(): Selection {
        return this.selection;
    }

    /**
     * 컬럼을 초기화한다.
     *
     * @param columns - 컬럼 설정 목록
     */
    initColumns(columns: ColumnProperties<T>[]): void {
        this.columns = [];
        for (const column of columns ?? []) {
            this.columns.push(new Column(column, this));
        }
        this.columns.forEach((column, index) => {
            column.setFreeze(index < this.freeze);
        });
        this.getColumns().forEach((column, index) => {
            column.setIndex(index);
        });
    }

    /**
     * 평탄화된 모든 컬럼 목록을 가져온다.
     *
     * @returns 평탄화된 모든 컬럼 목록
     */
    getColumns(): Column[] {
        return this.columns.flatMap((column) => column.toPlain());
    }

    /**
     * 인덱스로 컬럼을 가져온다.
     *
     * @param index - 컬럼 인덱스
     * @returns 컬럼
     */
    getColumnAt(index: number): Column {
        return this.getColumns()[index] ?? null;
    }

    /**
     * dataIndex 로 컬럼을 가져온다.
     *
     * @param dataIndex - 데이터인덱스
     * @returns 컬럼
     */
    getColumnByDataIndex(dataIndex: string): Column {
        return this.getColumns().find((column) => column.dataIndex === dataIndex) ?? null;
    }

    /**
     * 데이터를 정렬한다.
     *
     * @param dataIndex - 정렬할 데이터인덱스
     * @param direction - 정렬방향
     */
    async sort(dataIndex: string, direction: 'ASC' | 'DESC'): Promise<void> {
        await this.getStore().sort(dataIndex, direction);
    }

    /**
     * 데이터를 정렬방식을 변경한다.
     *
     * @param sorters - 정렬방식
     */
    async setSorters(sorters: { [field: string]: 'ASC' | 'DESC' | any[] }): Promise<void> {
        await this.getStore().setSorters(sorters);
    }

    /**
     * 데이터를 필터링한다.
     *
     * @param dataIndex - 필터를 적용할 데이터인덱스
     * @param value - 필터값
     * @param operator - 필터조건
     */
    async filter(dataIndex: string, value: any, operator: string = '='): Promise<void> {
        await this.getStore().filter(dataIndex, value, operator);
    }

    /**
     * 데이터 필터방식을 변경한다.
     *
     * @param filters - 필터방식
     * @param filterMode - 필터모드
     */
    async setFilters(
        filters: { [field: string]: { value: any; operator: string } | string } | null,
        filterMode: 'OR' | 'AND' = 'AND'
    ): Promise<void> {
        await this.getStore().setFilters(filters, filterMode);
    }

    /**
     * 데이터 그룹방식을 변경한다.
     *
     * @param dataIndex - 그룹핑 데이터인덱스 값
     * @param sorters - 그룹 정렬 방식
     * @param summary - 그룹 합계여부
     * @param renderer - 그룹명 렌더링
     */
    async group(
        dataIndex: string,
        sorters: Grouper<T>['sorters'],
        summary: boolean = false,
        renderer: Grouper<T>['renderer'] = null
    ): Promise<void> {
        this.getStore().setGroup({ field: dataIndex, sorters });
        this.grouper = { dataIndex, sorters, summary, renderer: renderer ?? null };
    }

    /**
     * 데이터 그룹방식을 해제한다.
     */
    async ungroup(): Promise<void> {
        this.getStore().setGroup(null);
        this.grouper = null;
    }

    /**
     * 현재 그룹설정방식을 가져온다.
     *
     * @returns 그룹설정
     */
    getGrouper(): Grouper<T> | null {
        return this.grouper;
    }

    /**
     * 그룹설정을 변경한다.
     *
     * @param grouper - 그룹설정
     */
    setGrouper(grouper: Grouper<T> | null): void {
        this.grouper = grouper;
    }

    /**
     * 고정 컬럼 수를 가져온다.
     *
     * @returns 고정 컬럼 수
     */
    getFreeze(): number {
        return this.freeze;
    }

    /**
     * 컬럼을 고정한다.
     *
     * @param freeze - 최상위 컬럼 기준 고정 컬럼 수
     */
    setFreeze(freeze: number): void {
        if (this.freeze !== freeze) {
            this.freeze = freeze;

            this.columns.forEach((column, index) => {
                column.setFreeze(index < this.freeze);
            });

            this.updateFreeze();
            this.getBody().setFreeze();
            this.getScroller().setTrackPosition('x', this.header?.$getFreeze()?.getWidth() ?? 0);
        }
    }

    /**
     * 고정열에 따른 컬럼 너비를 갱신한다.
     */
    updateFreeze(): void {
        this.freezeFlexBasis = { px: 0, rem: 0 };
        this.freezeFlexGrow = 0;
        this.freezeStyle = null;

        this.getColumns().forEach((column) => {
            if (column.isHidden() == true) {
                return;
            }

            if (column.freeze == true) {
                const basis = column.getChildrenFlexBasis();
                this.freezeFlexBasis.px += basis.px;
                this.freezeFlexBasis.rem += basis.rem;
                this.freezeFlexGrow += column.getChildrenFlexGrow();
            }
        });

        const basis: string[] = [];
        if (this.selection.multiple == true) {
            basis.push('var(--aui-component-height-default)');
        }

        basis.push(`${this.freezeFlexBasis.px + 1}px`);

        if (this.freezeFlexBasis.rem > 0) {
            basis.push(`${this.freezeFlexBasis.rem}rem`);
        }

        this.freezeStyle = 'flex-basis:calc( ' + basis.join(' + ') + ');';
        if (this.freezeFlexGrow > 0) {
            this.freezeStyle += 'flex-grow:' + this.freezeFlexGrow + ';';
        }

        this.$getContent().setStyleProperty('--freeze-basis', `calc(${basis.join(' + ')})`);
        this.$getContent().setStyleProperty('--freeze-grow', `${this.freezeFlexGrow}`);

        for (const column of this.getColumns()) {
            column.updateWidth();
        }

        this.header?.updateLayout();
    }

    /**
     * 데이터 형태가 트리인지 확인한다.
     *
     * @returns 트리여부
     */
    abstract isTree(): boolean;

    /**
     * 테이블을 렌더링한다.
     */
    async renderTable(): Promise<void> {
        this.$getContent().setStyleProperty('--aui-table-content-anchor-name', '--aui-table-content-' + this.getId());

        if (this.getHeader() !== null) {
            this.$getContent().append(this.getHeader().$getHeader());
            await this.getHeader().render();
        }
        this.updateFreeze();

        this.$getContent().append(this.getBody().$getBody());

        if (this.getSelection().isSelectView() === true) {
            this.$getContent().append(this.getSelection().$getSelectionView());

            if (this.getSelection().isAlwaysShow() != true) {
                this.getSelection().$getSelectionView().hide();
            }
        }
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        this.$getContainer().append(this.$getHeader());
        this.$getContainer().append(this.$getContent());
        this.$getContainer().append(this.$getFooter());

        await this.renderHeader();
        await this.renderFooter();
        await this.renderTable();

        if (this.properties.loading === true) {
            this.setLoading('column', Language.getAction('LOADING'));
        }
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();

        if (this.getHeader() !== null) {
            this.getScroller()?.addEvent('active', (scroller, direction) => {
                if (direction == 'y') {
                    if (this.getHeader().$getHeader().hasClass('stuck') == true) {
                        if (scroller.getScrollPosition().y === 0) {
                            this.getHeader().$getHeader().removeClass('stuck');
                        }
                    } else {
                        if (scroller.getScrollPosition().y > 0) {
                            this.getHeader().$getHeader().addClass('stuck');
                        }
                    }
                }
            });

            this.getScroller()?.setTrackPosition('x', this.getHeader().$getFreeze().getWidth());
        }

        this.getScroller()?.setTrackPosition('y', this.getHeader()?.$getHeader().getOuterHeight() ?? 0);

        if (this.autoLoad === true) {
            this.store.load();
        }
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (this.store !== null) {
            this.getStore().release();
            this.store = null;
        }
        this.body?.destroy();
        this.header?.destroy();
        this.selection?.destroy();
        this.body = null;
        this.header = null;
        this.selection = null;

        await super.remove(removeDom);
    }

    /**
     * 데이터스토어가 로딩되기 전
     */
    async onBeforeLoad(): Promise<void> {
        if (this.getStore().getProvider().isRemote() == true) {
            this.setLoading('column', Language.getAction('LOADING'));
        }
        this.fireEvent('beforeLoad', [this]);
        await Utils.sleep(100);
    }

    /**
     * 데이터스토어가 로딩되었을 때
     *
     * @param is_fire_event - 이벤트발생여부
     */
    async onLoad(is_fire_event: boolean = true): Promise<void> {
        await this.getHeader()?.update();
        await this.getBody().render();

        this.setLoading(false);

        if (is_fire_event == true) {
            await this.fireEvent('load', [this]);
            await this.fireEvent('update', [this]);
        }
    }

    /**
     * 데이터스토어가 로딩된 이후 로컬정렬, 로컬필터 등으로 데이터가 변경되었을 때
     */
    async onUpdate(): Promise<void> {
        await this.getHeader()?.update();
        await this.getBody().render(false);
        this.setLoading(false);

        this.fireEvent('update', [this]);
    }

    /**
     * 레코드가 변경되었을 때
     *
     * @param record - 변경된 레코드
     */
    async onEdit(record: Record): Promise<void> {
        if (this.summary == true) {
            this.getBody().summary.$getSummary(this.getStore().getRecords());
        }

        this.fireEvent('edit', [record, this]);
    }

    /**
     * 키보드 이벤트를 처리한다.
     *
     * @param e - 키보드 이벤트
     * @returns 상위 컴포넌트로 전파할지 여부 (false 인 경우 전파하지 않는다.)
     */
    async onKeyboard(e: KeyboardEvent): Promise<boolean> {
        await this.fireEvent('keyboard', [e, this]);
        return await this.body.onKeyboard(e);
    }
}
